import type {ReactNode} from 'react';
import {Component, isValidElement, useEffect} from 'react';

import {SIZE_BADGE} from '@app/features';
import {CacheProvider, Global, ThemeProvider} from '@emotion/react';
import styled from '@emotion/styled';
import {WithFeature} from '@nfq/feature-flags/jsx';
import {preloadFonts} from '@nfq/next-fonts';
import {ScreenBadge, ScreenSizeProvider} from '@nfq/react-grid';
import {AnimatePresence, LazyMotion} from 'motion/react';
import * as motion from 'motion/react-m';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import {useRouter} from 'next/router';

import {AppHeader} from 'UI/components/layout/AppHeader';
import {NavigationBar} from 'UI/components/layout/NavigationBar';
import {AppUpdateBanner} from 'UI/components/modules/AppUpdateBanner';
import {LanguagePopup} from 'UI/components/modules/LanguagePopup';
import {PwaInstallBanner} from 'UI/components/modules/PwaInstallBanner';

import i18n from 'GlobalShared/i18n';
import {LayoutTransition} from 'UI/animations/layout';
import {AuthProvider} from 'UI/hooks/useAuth';
import {DialogManagerProvider} from 'UI/hooks/useDialogManager';
import {PromoProvider} from 'UI/hooks/usePromo';
import {AppLayout} from 'UI/layouts/AppLayout';
import {useAppLayout} from 'UI/layouts/AppLayout/useAppLayout';
import {AuthLayout} from 'UI/layouts/AuthLayout';

import {createEmotionCache} from 'Application/configs/emotionCache';
import {fontDefinitions, globals, theme} from 'UI/utils/globalStyles';

import type {EmotionCache} from '@emotion/react';
import type {FeatureBundle, Transition} from 'motion/react';
import type {NextRouter} from 'next/router';
import type {AppProps} from 'types/global';
import type {AppLayoutProps} from 'UI/layouts/AppLayout';

import 'Fonts/fonts';

/**
 * `AxeCoreHelper` is a dynamic import of the `AxeCoreHelper` module from the 'UI/utils/AxeCoreHelper' path.
 * It is named dynamically by Webpack as "axeCoreHelper" and is not server-side rendered.
 * This import is crucial for utilizing the AxeCoreHelper utility functions and features within the application, especially for accessibility testing.
 *
 * @example
 * ```tsx
 * const AxeCoreHelper = AxeCoreHelper();
 *
 * <AxeCoreHelper />
 * ```
 */
const AxeCoreHelper = dynamic(async () => import(
    /* webpackChunkName: "axeCoreHelper" */
    'UI/utils/AxeCoreHelper/AxeCoreHelper'
), {ssr: false});

/**
 * `loadMotionFeatures` is an asynchronous function designed to dynamically import and return the default export of the `motionFeatures`
 * module from the 'UI/utils/motionFeatures' path.
 * It is named dynamically by Webpack as "motionFeatures".
 * This function is essential for loading motion-related features and utilities, allowing for enhanced user interface
 * interactions and animations within the application.
 *
 * @returns A promise resolving to the default export of the dynamically imported `motionFeatures` module, representing a bundle of motion-related features and utilities.
 *
 * @example
 * ```tsx
 * <LazyMotion features={loadMotionFeatures} strict>
 * ```
 */
const loadMotionFeatures = async (): Promise<FeatureBundle> => {
    const module = await import(
        /* webpackChunkName: "motionFeatures" */
        'UI/utils/motionFeatures'
    );

    return module.default;
};

const clientSideEmotionCache = createEmotionCache();

/**
 * Hook zum Synchronisieren der Next.js Router-Locale mit i18next.
 */
const useLocaleEffect = () => {
    const {locale} = useRouter();

    useEffect(() => {
        if (locale && i18n.language !== locale) {
            void i18n.changeLanguage(locale);
        }
    }, [locale]);
};

/**
 * Syncs the Next.js router locale with i18next language.
 *
 * @returns Null – renders nothing.
 */
const LocaleSync = () => {
    useLocaleEffect();

    return null;
};

LocaleSync.displayName = 'LocaleSync';

/**
 * Describes which persistent chrome (header/navigation) the current page needs.
 * Derived from the `AppLayout`/`AuthLayout` element a page's `getLayout` returns, so the
 * `AppHeader`/`NavigationBar` can be rendered once by `_app.tsx`, outside the page-transition
 * animation, instead of remounting and fading with every route change.
 */
type ChromeConfig
    = | {
        activeTab: string;
        hideNavBar: boolean;
        isBackVisible: boolean;
        onBack?(): void;
        realBackLink: boolean;
        type: 'app';
    }
    | {type: 'auth' | 'none'};

/**
 * Navigates one level up in the URL path hierarchy, or to the homepage. Extracted as a
 * standalone, top-level function (instead of a `handleBack` closure inside `PersistentHeader`)
 * so the component body stays reducible to its single `useRouter` hook call plus the return
 * statement, per the `@nfq/component-single-hook` rule.
 *
 * @param router       Der Next.js Router.
 * @param realBackLink Ob `router.back()` statt Pfad-Segment-Slicing verwendet werden soll.
 * @param onBack       Optionaler Override für die Zurück-Aktion.
 */
const navigateBack = (router: NextRouter, realBackLink: boolean, onBack?: () => void) => {
    if (realBackLink && window.history.length > 1) {
        router.back();

        return;
    } else if (onBack) {
        onBack();

        return;
    }

    const segments = router.asPath.split('?')[0].split('/').filter(Boolean);

    if (segments.length <= 1) {
        void router.push('/');
    } else {
        void router.push(`/${segments.slice(0, -1).join('/')}`);
    }
};

/**
 * The persistent top header, rendered once outside the page-transition animation so it no
 * longer fades out/in on navigation. Owns the back-navigation logic previously in `AppLayout`.
 *
 * @param props               The component props.
 * @param props.isBackVisible Whether to show the back arrow.
 * @param props.onBack        Callback for the header back button press. Defaults to `router.back()`.
 * @param props.realBackLink  Whether to use the real `router.back()` instead of path-segment slicing.
 * @returns A JSX element rendering the persistent AppHeader.
 */
const PersistentHeader = (
    {isBackVisible, onBack, realBackLink}: {isBackVisible: boolean; onBack?(): void; realBackLink: boolean}
) => {
    const router = useRouter();

    return (
        <ChromeColumn>
            <AppHeader isBackVisible={isBackVisible} onBack={() => navigateBack(router, realBackLink, onBack)} />
        </ChromeColumn>
    );
};

PersistentHeader.displayName = 'PersistentHeader';

/** Slide transition for the bottom navigation bar appearing/disappearing. */
const NAV_BAR_TRANSITION: Transition = {
    duration: 0.3,
    ease: 'easeInOut'
};

/**
 * The persistent bottom navigation bar, rendered once outside the page-transition animation so
 * it no longer fades out/in on navigation and its active-tab highlight can animate smoothly
 * between tabs. Slides up from below on its initial appearance and whenever a page needs it
 * again, and slides back down out of view on pages that hide it.
 *
 * @param props           The component props.
 * @param props.activeTab The key of the currently active navigation tab.
 * @returns A JSX element rendering the persistent NavigationBar.
 */
const PersistentNavBar = ({activeTab}: {activeTab: string}) => {
    const tabs = useAppLayout();

    return (
        <NavWrapper
            animate={{y: '0%'}}
            exit={{y: '100%'}}
            initial={{y: '100%'}}
            transition={NAV_BAR_TRANSITION}
        >
            <NavigationBar activeTab={activeTab} tabs={tabs} />
        </NavWrapper>
    );
};

PersistentNavBar.displayName = 'PersistentNavBar';

/**
 * The `App` class extends the `Component` class and serves as the main application component.
 * It is responsible for rendering the overall layout and structure of the application, including theming, global styles, and various providers.
 * This class is crucial for managing the overall appearance and behavior of the application and for wrapping the application with necessary context providers.
 */
class App extends Component<AppProps<object> & {emotionCache?: EmotionCache}> {
    static displayName = 'App';

    /**
     * `getLayoutKey` is a method designed to retrieve the layout key from the PageComponent.
     * It is responsible for determining which layout should be used when rendering the application.
     * This method is crucial for managing different layouts within the application and for providing a default layout when none is specified.
     *
     * @returns A string representing the layout key, defaulting to 'default' if no layout key is provided by the PageComponent.
     *
     * @example
     * ```tsx
     * const layoutKey = this.getLayoutKey();
     * ```
     */
    getLayoutKey(): string {
        const {Component: PageComponent} = this.props;

        return PageComponent.getLayoutKey?.() ?? 'default';
    }

    /**
     * `getChromeConfig` inspects the layout element a page's `getLayout` produced and derives
     * which persistent chrome (header/navigation) it needs. It reads the `AppLayout`/`AuthLayout`
     * element's props directly rather than requiring pages to declare chrome separately, so all
     * existing `getLayout` implementations keep working unchanged.
     *
     * @param layoutElement The element returned by `chooseLayout`.
     * @returns The chrome configuration for the current page.
     *
     * @example
     * ```tsx
     * const chrome = this.getChromeConfig(this.chooseLayout());
     * ```
     */
    getChromeConfig(layoutElement: ReactNode): ChromeConfig {
        if (isValidElement<AppLayoutProps>(layoutElement) && layoutElement.type === AppLayout) {
            const {
                activeTab, hideNavBar = false, isBackVisible = false, onBack, realBackLink = false
            } = layoutElement.props;

            return {
                activeTab,
                hideNavBar,
                isBackVisible,
                onBack,
                realBackLink,
                type: 'app'
            };
        }

        if (isValidElement(layoutElement) && layoutElement.type === AuthLayout) {
            return {type: 'auth'};
        }

        return {type: 'none'};
    }

    /**
     * `chooseLayout` is a method designed to determine and return the appropriate layout based on the PageComponent.
     * It checks if the PageComponent has a `getLayout` method and calls it if present, otherwise, it returns the PageComponent itself.
     * This method is essential for rendering the correct layout based on the current page and its properties.
     *
     * @returns A ReactNode representing the chosen layout or the PageComponent.
     *
     * @example
     * ```tsx
     * const chosenLayout = this.chooseLayout();
     * ```
     */
    chooseLayout(): ReactNode {
        const {Component: PageComponent, pageProps, router} = this.props;

        if (PageComponent.getLayout) {
            return PageComponent.getLayout(router, pageProps, PageComponent);
        }

        // eslint-disable-next-line react/jsx-props-no-spreading
        return <PageComponent {...pageProps} />;
    }

    /**
     * The `render` method is responsible for rendering the overall structure and elements of the application.
     * It includes various providers, global styles, and components necessary for the application to function correctly.
     * This method is crucial for defining the overall appearance and structure of the application.
     *
     * @returns A ReactNode representing the overall structure and elements of the application.
     */
    render(): ReactNode {
        const {emotionCache = clientSideEmotionCache} = this.props;
        const layoutElement = this.chooseLayout();
        const chrome = this.getChromeConfig(layoutElement);

        return (
            <CacheProvider value={emotionCache}>
                <AuthProvider>
                    <PromoProvider>
                        <ThemeProvider theme={theme}>
                            <Head>
                                <title>little walks</title>
                                <meta content="initial-scale=1.0, width=device-width" name="viewport" />
                                {preloadFonts(fontDefinitions)}
                            </Head>
                            <Global styles={globals} />
                            <AxeCoreHelper />
                            <LocaleSync />
                            <DialogManagerProvider>
                                <AppUpdateBanner />
                                <LanguagePopup />
                                <PwaInstallBanner />
                            </DialogManagerProvider>
                            <ScreenSizeProvider>
                                <LazyMotion features={loadMotionFeatures} strict>
                                    {chrome.type !== 'none' && (
                                        <PersistentHeader
                                            isBackVisible={chrome.type === 'app' ? chrome.isBackVisible : false}
                                            realBackLink={chrome.type === 'app' ? chrome.realBackLink : false}
                                            onBack={chrome.type === 'app' ? chrome.onBack : undefined}
                                        />
                                    )}
                                    <AnimatePresence initial={false} mode="wait">
                                        <AnimationWrapper
                                            key={this.getLayoutKey()}
                                            animate="enter"
                                            exit="exit"
                                            initial="initial"
                                            variants={LayoutTransition}
                                        >
                                            {layoutElement}
                                        </AnimationWrapper>
                                    </AnimatePresence>
                                    <AnimatePresence>
                                        {chrome.type === 'app' && !chrome.hideNavBar && (
                                            <PersistentNavBar key="navBar" activeTab={chrome.activeTab} />
                                        )}
                                    </AnimatePresence>
                                </LazyMotion>
                                <WithFeature feature={SIZE_BADGE}><ScreenBadge /></WithFeature>
                            </ScreenSizeProvider>
                        </ThemeProvider>
                    </PromoProvider>
                </AuthProvider>
            </CacheProvider>
        );
    }
}

export default App;

export {PersistentHeader};

const ChromeColumn = styled.div`
    margin: 0 auto;
    max-width: 1080px;
    width: 100%;
`;

const NavWrapper = styled(motion.div)`
    backdrop-filter: blur(20px);
    background: ${({theme: navTheme}) => navTheme.colors.surfaceBackground}b3;
    border-top: 1px solid ${({theme: navTheme}) => navTheme.colors.inkDisabled}33;
    bottom: 0;
    box-shadow: ${({theme: navTheme}) => navTheme.boxShadows.floatingShadow};
    left: 0;
    position: fixed;
    right: 0;
    z-index: 50;
`;

const AnimationWrapper = styled(motion.div)`
    margin: 0 auto;
    max-width: 1080px;
    width: 100%;
`;